import type { ResolvedInterval, SchedulePrototype, ScheduleResolved } from '@bp/utils';
import classNames from 'classnames';
import type { Duration } from 'luxon';
import { DateTime } from 'luxon';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
interface ScheduleInfoProps {
    schedule: ScheduleResolved | null | undefined;
    schedulePrototype?: SchedulePrototype | null;
    logicState?: string;
    showCurrentInterval?: boolean | 'relative';
    relativeDays?: number;
}

function formatDuration(durOrig: Duration): string {
    const dur = durOrig.shiftTo('days', 'hours', 'minutes');

    if (dur.days >= 1) {
        return ` in ${Math.floor(dur.days)}d ${Math.floor(dur.hours)}h`;
    } else if (dur.hours >= 1) {
        return ` in ${Math.floor(dur.hours)}h ${Math.floor(dur.minutes)}m`;
    } else return ` in ${Math.floor(dur.minutes)}m`;
}

interface ScheduleIntervalProps {
    interval: ResolvedInterval | null | undefined;
    asRelative?: boolean;
    soonIntervalMinutes?: number;
    considerInternalPassedIfEndingSoon?: boolean;
    showTimeOnly?: boolean;
}

const IrregularIcon = () => (
    <span title="Irregular interval">
        <i className="bi bi-exclamation-triangle-fill text-warning" style={{ marginRight: 4 }}></i>
    </span>
);

function ScheduleInterval({ interval, asRelative = false, soonIntervalMinutes = 30, showTimeOnly = false }: ScheduleIntervalProps) {
    if (!interval) return <span>-</span>;

    const start = DateTime.fromISO(interval.start);
    const end = DateTime.fromISO(interval.end);
    const startWithZone = DateTime.fromISO(interval.start, { setZone: true });

    if (asRelative) {
        const startDiff = start.diffNow().as('minutes');
        const endDiff = end.diffNow().as('minutes');

        const beforeStart = startDiff > 0;
        const afterEnd = endDiff < 0;
        const inInterval = !beforeStart && !afterEnd;

        // "Soon" logic
        const startingSoon = beforeStart && startDiff <= soonIntervalMinutes;
        const endingSoon = inInterval && endDiff <= soonIntervalMinutes;

        return (
            <>
                {interval.irregularity ? <IrregularIcon /> : null}
                {beforeStart && <span className={startingSoon ? 'text-danger' : undefined}>starting {formatDuration(start.diffNow())}</span>}
                {inInterval && <span className={endingSoon ? 'text-success' : undefined}>ending {formatDuration(end.diffNow())}</span>}
                {afterEnd && <span>ended {formatDuration(end.diffNow().negate())}</span>}
            </>
        );
    }

    const nextDayEnd = end.day != start.day;

    const configFullStart = { weekday: 'short', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' } as const;
    const configTimeOnly = { hour: '2-digit', minute: '2-digit' } as const;

    return (
        <>
            {interval.irregularity ? <IrregularIcon /> : null}
            <OverlayTrigger
                overlay={
                    <Tooltip id={`tooltip-${start.toISO()}`}>
                        <div>
                            {start.setZone(startWithZone.zone).toLocaleString(showTimeOnly ? configTimeOnly : configFullStart)} -{' '}
                            {end.setZone(startWithZone.zone).toLocaleString(configTimeOnly)}
                            {nextDayEnd ? '+1d' : ''} {startWithZone.zone.offsetName(start.toMillis(), { format: 'short' })}
                        </div>
                    </Tooltip>
                }
            >
                <span>
                    {start.toLocaleString(showTimeOnly ? configTimeOnly : configFullStart)} - {end.toLocaleString(configTimeOnly)}
                    {nextDayEnd ? '+1d' : ''} {start.zone.offsetName(start.toMillis(), { format: 'short' })}
                </span>
            </OverlayTrigger>
        </>
    );
}

function ScheduleDayRow({ dateEntry }: { dateEntry: ScheduleResolved['dayIntervals'][number] }) {
    const date = DateTime.fromISO(dateEntry.date);
    const isToday = date.hasSame(DateTime.now(), 'day');
    const intervalsForDay = dateEntry.intervals;
    const dayLabel = date.toFormat('ccc dd. LLL');
    const isIrregularDay = dateEntry.irregularities && dateEntry.irregularities.length > 0;

    return (
        <div
            className={classNames({ 'text-info': isToday })}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 4,
                fontWeight: isToday ? 'bold' : undefined
            }}
        >
            <div style={{ minWidth: 100, textAlign: 'left', fontWeight: 'bold' }}>{dayLabel}</div>
            <div style={{ flex: 1, textAlign: 'left' }}>
                {intervalsForDay.length > 0 ? (
                    intervalsForDay.map((interval, index) => (
                        <span key={index}>
                            <ScheduleInterval interval={interval} showTimeOnly />
                            {index < intervalsForDay.length - 1 && ', '}
                        </span>
                    ))
                ) : (
                    <span>{isIrregularDay && <IrregularIcon />} Closed</span>
                )}
                {isIrregularDay && <span className="text-warning"> {dateEntry.irregularities?.join(', ')}</span>}
            </div>
        </div>
    );
}

function ScheduleForUpcomingDays({
    schedule,
    beginOnWeekFirstDay = true,
    absoluteDays = 8,
    relativeDays,
    showUpcomingIrregularDaysInFooter
}: {
    schedule: ScheduleResolved;
    beginOnWeekFirstDay?: boolean;
    absoluteDays?: number;
    relativeDays?: number;
    showUpcomingIrregularDaysInFooter?: number;
}) {
    // Determine the anchor date. If relativeDays is provided, we offset from today.
    const anchorDate = relativeDays !== undefined ? DateTime.now().startOf('day').plus({ days: relativeDays }) : DateTime.now().startOf('day');

    // If beginOnWeekFirstDay is true, we want to start from the beginning of the week relative to the anchor date.
    // Otherwise, we start from the anchor date itself.
    const startDate = beginOnWeekFirstDay ? anchorDate.startOf('week') : anchorDate;

    const futureIntervals = schedule.dayIntervals.filter((dateEntry) => DateTime.fromISO(dateEntry.date) >= startDate);
    const mainList = futureIntervals.slice(0, absoluteDays);
    const footerList = showUpcomingIrregularDaysInFooter
        ? futureIntervals
              .slice(absoluteDays)
              .filter((d) => (d.irregularities && d.irregularities.length > 0) || d.intervals.some((i) => i.irregularity))
              .slice(0, showUpcomingIrregularDaysInFooter)
        : [];

    return (
        <>
            {mainList.map((dateEntry) => (
                <ScheduleDayRow key={dateEntry.date} dateEntry={dateEntry} />
            ))}
            {footerList.length > 0 && (
                <>
                    <hr className="my-2" style={{ borderColor: 'rgba(255,255,255,1)' }} />
                    {footerList.map((dateEntry) => (
                        <ScheduleDayRow key={dateEntry.date} dateEntry={dateEntry} />
                    ))}
                </>
            )}
        </>
    );
}

export function ScheduleInfo({ schedule, logicState }: ScheduleInfoProps) {
    if (!schedule) {
        return <span>No schedule</span>;
    }

    const breakBetweenIntervals = Math.abs(
        DateTime.fromISO(schedule.currentInterval?.end ?? '')
            .diff(DateTime.fromISO(schedule.nextInterval?.start ?? ''))
            .as('minutes')
    );

    const hasIrregularities: boolean = schedule.dayIntervals
        .filter((dateEntry) => DateTime.fromISO(dateEntry.date) >= DateTime.now().startOf('day'))
        .some((day) => day.irregularities && day.irregularities.length > 0);

    return (
        <>
            <OverlayTrigger
                placement="auto"
                overlay={
                    <Tooltip className="custom-tooltip" id="schedule-overview-tooltip2">
                        <ScheduleForUpcomingDays schedule={schedule} beginOnWeekFirstDay={true} absoluteDays={7} showUpcomingIrregularDaysInFooter={3} />
                    </Tooltip>
                }
            >
                <span>
                    <i
                        className={classNames('bi bi-calendar2-week', { 'text-info': !hasIrregularities, 'text-danger': hasIrregularities })}
                        style={{ marginRight: 4 }}
                    ></i>
                </span>
            </OverlayTrigger>
            <div>
                <div>
                    <ScheduleInterval
                        interval={schedule.currentInterval ?? schedule.nextInterval}
                        asRelative={true}
                        considerInternalPassedIfEndingSoon={logicState !== 'running'}
                    />
                    {breakBetweenIntervals < 30 && <span> continuous</span>}
                </div>
                <div className={classNames({ 'text-muted': true })}>
                    <span className={classNames({ 'd-none': !schedule.currentInterval })}>next: </span>
                    <ScheduleInterval interval={schedule.nextInterval} asRelative={false} />
                </div>
            </div>
        </>
    );
}
